import { AxiosError } from 'axios';

/** Default (Tamil) UI copy for TENANT_LESSONS_NOT_FOUND. Prefer tenant-aware mapping via options. */
export const TENANT_LESSONS_NOT_FOUND_UI_MESSAGE =
  'No Tamil Tutor lessons are assigned to your school yet. Please contact your school administrator.';

export type ApiErrorMessageOptions = {
  /** Tenant-aware empty-lessons copy (Malay POC Phase 1B). */
  emptyLessonsMessage?: string;
};

type ApiDetailObject = {
  ok?: boolean;
  error?: string;
  code?: string;
  message?: string;
};

function readDetailObject(data: unknown): ApiDetailObject | null {
  if (!data || typeof data !== 'object') return null;
  const root = data as Record<string, unknown>;
  const detail = root.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail as ApiDetailObject;
  }
  return null;
}

function readCodeFromData(data: unknown): string | undefined {
  const detail = readDetailObject(data);
  if (typeof detail?.error === 'string' && detail.error.trim()) return detail.error.trim();
  if (typeof detail?.code === 'string' && detail.code.trim()) return detail.code.trim();

  if (!data || typeof data !== 'object') return undefined;
  const root = data as Record<string, unknown>;
  if (typeof root.error === 'string' && root.error.trim()) return root.error.trim();
  const nested = root.error;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const code = (nested as Record<string, unknown>).code;
    if (typeof code === 'string' && code.trim()) return code.trim();
  }
  if (typeof root.error_code === 'string' && root.error_code.trim()) return root.error_code.trim();
  return undefined;
}

function readBackendMessageFromData(data: unknown): string | undefined {
  const detail = readDetailObject(data);
  if (typeof detail?.message === 'string' && detail.message.trim()) return detail.message.trim();

  if (!data || typeof data !== 'object') return undefined;
  const root = data as Record<string, unknown>;
  // FastAPI often returns {"detail": "CODE_OR_MESSAGE"} as a plain string.
  if (typeof root.detail === 'string' && root.detail.trim()) return root.detail.trim();
  if (typeof root.message === 'string' && root.message.trim()) return root.message.trim();
  const nested = root.error;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const message = (nested as Record<string, unknown>).message;
    if (typeof message === 'string' && message.trim()) return message.trim();
  }
  return undefined;
}

function readTraceIdFromData(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const traceId = (data as Record<string, unknown>).trace_id;
  if (typeof traceId === 'string' && traceId.trim()) return traceId.trim();
  return undefined;
}

export function getApiErrorCode(error: unknown): string | undefined {
  if (error instanceof AxiosError) {
    return readCodeFromData(error.response?.data);
  }
  return undefined;
}

export function getApiErrorBackendMessage(error: unknown): string | undefined {
  if (error instanceof AxiosError) {
    return readBackendMessageFromData(error.response?.data);
  }
  if (error instanceof Error) {
    return error.message;
  }
  return undefined;
}

export function getApiErrorTraceId(error: unknown): string | undefined {
  if (error instanceof AxiosError) {
    return readTraceIdFromData(error.response?.data);
  }
  return undefined;
}

export function logApiErrorDiagnostics(error: unknown, context?: string): void {
  const code = getApiErrorCode(error);
  const backendMessage = getApiErrorBackendMessage(error);
  const traceId = getApiErrorTraceId(error);
  if (!code && !backendMessage && !traceId) return;

  const payload: Record<string, string | undefined> = {
    code,
    backendMessage,
    trace_id: traceId,
  };
  if (import.meta.env.DEV) {
    console.debug(`[api-error] ${context ?? 'request'}`, payload);
  } else if (traceId) {
    console.debug(`[api-error] trace_id: ${traceId}`);
  }
}

/** Map structured response JSON (axios body or fetch JSON) to a user-facing message. */
export function getApiErrorMessageFromResponseData(
  data: unknown,
  options?: ApiErrorMessageOptions
): string | null {
  const code = readCodeFromData(data);
  const backendMessage = readBackendMessageFromData(data);
  const traceId = readTraceIdFromData(data);

  if (code || backendMessage || traceId) {
    const payload: Record<string, string | undefined> = {
      code,
      backendMessage,
      trace_id: traceId,
    };
    if (import.meta.env.DEV) {
      console.debug('[api-error] response body', payload);
    } else if (traceId) {
      console.debug(`[api-error] trace_id: ${traceId}`);
    }
  }

  if (code === 'TENANT_LESSONS_NOT_FOUND') {
    if (backendMessage) {
      console.debug('[api-error] TENANT_LESSONS_NOT_FOUND backend message:', backendMessage);
    }
    return options?.emptyLessonsMessage?.trim() || TENANT_LESSONS_NOT_FOUND_UI_MESSAGE;
  }

  if (backendMessage) return backendMessage;
  return null;
}

export function getApiErrorMessage(error: unknown, options?: ApiErrorMessageOptions): string {
  if (error instanceof AxiosError) {
    logApiErrorDiagnostics(error);
    const fromBody = getApiErrorMessageFromResponseData(error.response?.data, options);
    if (fromBody) return fromBody;

    const axiosMsg = error.message?.trim();
    if (axiosMsg?.startsWith('Request failed with status code')) {
      return 'Something went wrong. Please try again.';
    }
    if (axiosMsg) return axiosMsg;
    return 'Something went wrong. Please try again.';
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return 'Something went wrong. Please try again.';
}
